import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PresenceInput } from '../../components/ui/presence-input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import OrganisationLookup from '../../components/ui/organisation-lookup';
import RecordHistoryButton from '../../components/history/RecordHistoryButton';
import CollaborationIndicator from '../../components/ui/collaboration-indicator';
import RelationshipSummary from '../../components/graphs/RelationshipSummary';
import { useOrganisationCollaboration } from '../../hooks/useOrganisationCollaboration';
import {
  createCustomerOrganisation,
  getOrganisationMembers,
  getOrganisationHierarchy,
  dataApi
} from '../../services/api';
import { Building2, Trash2, User } from 'lucide-react';

export const ROLE_OPTIONS = [
  { value: 'customer', label: 'Customer' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'subcontractor', label: 'Subcontractor' }
];

const CustomerOrganisationForm = ({ organisationId, isOpen, onClose, onSave, initialName = '' }) => {
  const [members, setMembers] = useState([]);
  const [hierarchy, setHierarchy] = useState({ parent: null, children: [] });
  const [regions, setRegions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const isEdit = !!organisationId;

  // Use collaboration hook for realtime autosave (edit mode only)
  const {
    data: org,
    loading,
    activeUsers,
    currentUser,
    isOnline,
    updateField,
    trackFieldEdit,
    clearFieldEdit,
    getFieldEditor,
    isCurrentUserEditing
  } = useOrganisationCollaboration(isEdit ? organisationId : null);

  // Local state for create mode only
  const [createForm, setCreateForm] = useState({
    name: '',
    tradingName: '',
    roles: 'customer',
    phones: [],
    emails: [],
    website: '',
    street1: '',
    street2: '',
    suburb: '',
    region: '',
    postcode: '',
    notes: '',
    parentOrganisationId: ''
  });

  // Reset create form when dialog opens
  useEffect(() => {
    if (isOpen && !isEdit) {
      setCreateForm(prev => ({ ...prev, name: initialName }));
    }
  }, [isOpen, isEdit, initialName]);

  // Load supplementary data (regions, members, hierarchy)
  const loadSupplementaryData = useCallback(async () => {
    try {
      const regionsData = await dataApi.regionList().catch(() => []);
      setRegions(regionsData || []);

      if (organisationId) {
        const [membersData, hier] = await Promise.all([
          getOrganisationMembers(organisationId).catch(() => []),
          getOrganisationHierarchy(organisationId).catch(() => ({ parent: null, children: [] }))
        ]);
        setMembers(membersData || []);
        setHierarchy(hier);
      } else {
        setMembers([]);
        setHierarchy({ parent: null, children: [] });
      }
    } catch (err) {
      console.error('Error loading supplementary data:', err);
    }
  }, [organisationId]);

  useEffect(() => {
    if (isOpen) loadSupplementaryData();
  }, [isOpen, loadSupplementaryData]);

  // Get current data source (collaboration hook for edit, local state for create)
  const formData = isEdit ? org : createForm;

  const rolesArray = formData?.roles ? formData.roles.split(',').map((r) => r.trim()).filter(Boolean) : [];

  // Field update handler — routes to collaboration hook or local state
  const handleFieldChange = (field, value) => {
    if (isEdit) {
      updateField(field, value);
    } else {
      setCreateForm(prev => ({ ...prev, [field]: value }));
    }
  };

  const toggleRole = (role) => {
    const updated = rolesArray.includes(role)
      ? rolesArray.filter((r) => r !== role)
      : [...rolesArray, role];
    handleFieldChange('roles', updated.join(','));
  };

  // Phone helpers
  const phones = Array.isArray(formData?.phones) ? formData.phones : [];
  const updatePhone = (idx, field, val) => {
    const updated = [...phones];
    updated[idx] = { ...updated[idx], [field]: val };
    handleFieldChange('phones', updated);
  };
  const removePhone = (idx) => handleFieldChange('phones', phones.filter((_, i) => i !== idx));
  const handleBlankPhoneChange = (field, val) => {
    if (val.trim()) {
      const newPhone = { number: '', description: '' };
      newPhone[field] = val;
      handleFieldChange('phones', [...phones, newPhone]);
    }
  };

  // Email helpers
  const emails = Array.isArray(formData?.emails) ? formData.emails : [];
  const updateEmail = (idx, field, val) => {
    const updated = [...emails];
    updated[idx] = { ...updated[idx], [field]: val };
    handleFieldChange('emails', updated);
  };
  const removeEmail = (idx) => handleFieldChange('emails', emails.filter((_, i) => i !== idx));
  const handleBlankEmailChange = (field, val) => {
    if (val.trim()) {
      const newEmail = { email: '', description: '' };
      newEmail[field] = val;
      handleFieldChange('emails', [...emails, newEmail]);
    }
  };

  const handleParentSelect = (selectedOrg) => {
    handleFieldChange('parentOrganisationId', selectedOrg?.$id || '');
  };

  // Create mode save
  const handleCreate = async () => {
    if (!createForm.name.trim()) {
      setError('Organisation name is required');
      return;
    }
    if (!createForm.roles) {
      setError('Select at least one role');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const result = await createCustomerOrganisation(createForm);
      onSave?.(result);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create organisation');
      console.error('Error creating organisation:', err);
    } finally {
      setCreating(false);
    }
  };

  // Loading state
  if (isEdit && loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Loading Organisation...</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-3/4" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col animate-none">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DialogTitle>
                {isEdit
                  ? `Organisation: ${formData?.name || ''}`
                  : 'New Organisation'}
              </DialogTitle>
              {isEdit && (
                <CollaborationIndicator
                  isOnline={isOnline}
                  activeUsers={activeUsers}
                  currentUserId={currentUser?.id}
                  align="left"
                />
              )}
              {isEdit && <RecordHistoryButton rowId={organisationId} title={formData?.name || 'Organisation'} />}
            </div>
            {!isEdit && (
              <Button onClick={handleCreate} disabled={creating} size="sm">
                {creating ? 'Creating...' : 'Create'}
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 pr-1">
          {/* Organisation Details */}
          <Card>
            <CardHeader>
              <CardTitle>Organisation Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="orgName">Name <span className="text-destructive">*</span></Label>
                  {isEdit ? (
                    <PresenceInput
                      id="orgName"
                      className="input-borderless"
                      value={formData?.name || ''}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      onFocus={() => trackFieldEdit('name')}
                      onBlur={clearFieldEdit}
                      editor={getFieldEditor('name')}
                      isCurrentUser={isCurrentUserEditing('name')}
                    />
                  ) : (
                    <Input
                      id="orgName"
                      value={formData?.name || ''}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tradingName">Trading Name</Label>
                  {isEdit ? (
                    <PresenceInput
                      id="tradingName"
                      className="input-borderless"
                      value={formData?.tradingName || ''}
                      onChange={(e) => handleFieldChange('tradingName', e.target.value)}
                      onFocus={() => trackFieldEdit('tradingName')}
                      onBlur={clearFieldEdit}
                      editor={getFieldEditor('tradingName')}
                      isCurrentUser={isCurrentUserEditing('tradingName')}
                    />
                  ) : (
                    <Input
                      id="tradingName"
                      value={formData?.tradingName || ''}
                      onChange={(e) => handleFieldChange('tradingName', e.target.value)}
                    />
                  )}
                </div>
              </div>

              {/* Roles */}
              <div className="space-y-2">
                <Label>Roles <span className="text-destructive">*</span></Label>
                <div className="flex gap-4">
                  {ROLE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <Checkbox
                        checked={rolesArray.includes(opt.value)}
                        onCheckedChange={() => toggleRole(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  {isEdit ? (
                    <PresenceInput
                      id="website"
                      className="input-borderless"
                      value={formData?.website || ''}
                      onChange={(e) => handleFieldChange('website', e.target.value)}
                      onFocus={() => trackFieldEdit('website')}
                      onBlur={clearFieldEdit}
                      editor={getFieldEditor('website')}
                      isCurrentUser={isCurrentUserEditing('website')}
                    />
                  ) : (
                    <Input
                      id="website"
                      value={formData?.website || ''}
                      onChange={(e) => handleFieldChange('website', e.target.value)}
                    />
                  )}
                </div>
              </div>

              {/* Parent Organisation */}
              <OrganisationLookup
                label="Parent Organisation"
                value={hierarchy.parent}
                onChange={handleParentSelect}
              />
            </CardContent>
          </Card>

          {/* Contact Details — blank line pattern */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Phones */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Phone Numbers</Label>
                {phones.map((p, idx) => (
                  <div key={idx} className="group/item flex gap-2 items-center">
                    <Input
                      value={p.number}
                      onChange={(e) => updatePhone(idx, 'number', e.target.value)}
                      className="flex-1 h-8 text-sm"
                    />
                    <Input
                      value={p.description}
                      onChange={(e) => updatePhone(idx, 'description', e.target.value)}
                      className="w-36 h-8 text-sm"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removePhone(idx)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                {/* Blank row for adding */}
                <div className="flex gap-2 items-center">
                  <Input
                    value=""
                    onChange={(e) => handleBlankPhoneChange('number', e.target.value)}
                    placeholder="Add phone..."
                    className="flex-1 h-8 text-sm border-dashed"
                  />
                  <Input
                    value=""
                    onChange={(e) => handleBlankPhoneChange('description', e.target.value)}
                    placeholder="Label"
                    className="w-36 h-8 text-sm border-dashed"
                  />
                  <div className="h-8 w-8 flex-shrink-0" />
                </div>
              </div>

              {/* Emails */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Email Addresses</Label>
                {emails.map((em, idx) => (
                  <div key={idx} className="group/item flex gap-2 items-center">
                    <Input
                      type="email"
                      value={em.email}
                      onChange={(e) => updateEmail(idx, 'email', e.target.value)}
                      className="flex-1 h-8 text-sm"
                    />
                    <Input
                      value={em.description}
                      onChange={(e) => updateEmail(idx, 'description', e.target.value)}
                      className="w-36 h-8 text-sm"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeEmail(idx)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                {/* Blank row for adding */}
                <div className="flex gap-2 items-center">
                  <Input
                    value=""
                    onChange={(e) => handleBlankEmailChange('email', e.target.value)}
                    placeholder="Add email..."
                    className="flex-1 h-8 text-sm border-dashed"
                  />
                  <Input
                    value=""
                    onChange={(e) => handleBlankEmailChange('description', e.target.value)}
                    placeholder="Label"
                    className="w-36 h-8 text-sm border-dashed"
                  />
                  <div className="h-8 w-8 flex-shrink-0" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle>Address</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="street1">Street Address 1</Label>
                {isEdit ? (
                  <PresenceInput
                    id="street1"
                    className="input-borderless"
                    value={formData?.street1 || ''}
                    onChange={(e) => handleFieldChange('street1', e.target.value)}
                    onFocus={() => trackFieldEdit('street1')}
                    onBlur={clearFieldEdit}
                    editor={getFieldEditor('street1')}
                    isCurrentUser={isCurrentUserEditing('street1')}
                  />
                ) : (
                  <Input id="street1" value={formData?.street1 || ''} onChange={(e) => handleFieldChange('street1', e.target.value)} />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="street2">Street Address 2</Label>
                {isEdit ? (
                  <PresenceInput
                    id="street2"
                    className="input-borderless"
                    value={formData?.street2 || ''}
                    onChange={(e) => handleFieldChange('street2', e.target.value)}
                    onFocus={() => trackFieldEdit('street2')}
                    onBlur={clearFieldEdit}
                    editor={getFieldEditor('street2')}
                    isCurrentUser={isCurrentUserEditing('street2')}
                  />
                ) : (
                  <Input id="street2" value={formData?.street2 || ''} onChange={(e) => handleFieldChange('street2', e.target.value)} />
                )}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="suburb">Suburb</Label>
                  {isEdit ? (
                    <PresenceInput
                      id="suburb"
                      className="input-borderless"
                      value={formData?.suburb || ''}
                      onChange={(e) => handleFieldChange('suburb', e.target.value)}
                      onFocus={() => trackFieldEdit('suburb')}
                      onBlur={clearFieldEdit}
                      editor={getFieldEditor('suburb')}
                      isCurrentUser={isCurrentUserEditing('suburb')}
                    />
                  ) : (
                    <Input id="suburb" value={formData?.suburb || ''} onChange={(e) => handleFieldChange('suburb', e.target.value)} />
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="region">Region</Label>
                  <Select value={formData?.region || undefined} onValueChange={(v) => handleFieldChange('region', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Region" />
                    </SelectTrigger>
                    <SelectContent>
                      {regions.filter((r) => r.region && r.region.trim()).map((r, i) => (
                        <SelectItem key={`${r.region}-${i}`} value={r.region}>{r.region}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postcode">Postcode</Label>
                  {isEdit ? (
                    <PresenceInput
                      id="postcode"
                      className="input-borderless"
                      value={formData?.postcode || ''}
                      onChange={(e) => handleFieldChange('postcode', e.target.value)}
                      onFocus={() => trackFieldEdit('postcode')}
                      onBlur={clearFieldEdit}
                      editor={getFieldEditor('postcode')}
                      isCurrentUser={isCurrentUserEditing('postcode')}
                    />
                  ) : (
                    <Input id="postcode" value={formData?.postcode || ''} onChange={(e) => handleFieldChange('postcode', e.target.value)} />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                className="w-full min-h-[80px] rounded-md border bg-background px-3 py-2 text-sm"
                value={formData?.notes || ''}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
              />
            </CardContent>
          </Card>

          {/* Members (edit mode only) */}
          {isEdit && members.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Contacts ({members.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {members.map((link) => (
                  <div key={link.$id} className="flex items-center justify-between p-2 border rounded-md">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {link.customer
                          ? `${link.customer.firstName || ''} ${link.customer.lastName || ''}`
                          : 'Unknown'}
                      </span>
                      {link.role && (
                        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          {link.role}
                        </span>
                      )}
                    </div>
                    {link.isPrimaryContact && (
                      <span className="text-xs text-primary font-medium">Primary</span>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Relationships (edit mode only) */}
          {isEdit && (
            <Card>
              <CardHeader>
                <CardTitle>Relationships</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <RelationshipSummary
                  entityType="organisation"
                  entityId={organisationId}
                />
                {hierarchy.children && hierarchy.children.length > 0 && (
                  <div className="mt-4">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Child Organisations</Label>
                    <div className="space-y-1 mt-1">
                      {hierarchy.children.map((child) => (
                        <div key={child.$id} className="flex items-center gap-2 p-2 border rounded-md">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">{child.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {error && (
            <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerOrganisationForm;